import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Header from 'components/Header';
import Footer from 'components/Footer';

import { useReveal, cx } from 'pages/Homepage/hooks';

import dareIcon from 'assets/purpose/1.svg';
import growIcon from 'assets/purpose/2.svg';
import liftIcon from 'assets/purpose/3.svg';

// Principle card with a staggered fade-and-rise entrance on scroll.
// The reveal lives on an outer wrapper so it never competes with the
// inner article's hover-lift transform.
const PrincipleCard = ({ index, title, icon, description }) => {
  const [ref, seen] = useReveal();
  return (
    <div ref={ref} className={cx('reveal h-full', seen && 'in')} style={{ transitionDelay: `${index * 120}ms` }}>
      <article className="flex h-full flex-col items-center rounded-2xl border border-line bg-white px-8 pt-10 pb-9 text-center transition-transform duration-150 hover:-translate-y-[3px] hover:shadow-[0_22px_44px_-28px_rgba(20,18,12,.28)]">
        <div className="mb-6 grid h-[92px] w-[92px] place-items-center">
          <img src={icon} alt="" className="h-full w-full object-contain" />
        </div>
        <h3 className="mb-3.5 font-display text-[26px] font-extrabold tracking-[0.02em] text-ink">
          {title}
        </h3>
        <p className="text-[15.5px] leading-[1.6] text-muted">{description}</p>
      </article>
    </div>
  );
};

const PurposePage = () => {
  const { t } = useTranslation();

  const principles = [
    { title: 'DARE', icon: dareIcon, description: t('purpose.dare_description') },
    { title: 'GROW', icon: growIcon, description: t('purpose.grow_description') },
    { title: 'LIFT', icon: liftIcon, description: t('purpose.lift_description') },
  ];

  // p1–p6 keep flowing in the serif reading column; the first
  // paragraph gets a drop-cap via the `first-letter:` utilities.
  const paragraphs = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'];

  return (
    <div className="purpose-page min-h-screen flex flex-col bg-paper">
      <Header />

      <main className="flex-1">
        {/* ============ COVER (yellow billboard) ============ */}
        <section className="relative overflow-hidden bg-brand text-ink border-b border-black/[0.06] px-9 py-20 sm:py-24">
          {/* paper-texture highlights so the yellow isn't flat */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 60% 70% at 16% 8%, rgba(255,255,255,.38), transparent 60%),' +
                'radial-gradient(ellipse 70% 60% at 92% 100%, rgba(0,0,0,.07), transparent 60%)',
            }}
          />
          <div className="relative mx-auto max-w-[1000px] text-center">
            <h1 className="font-sans font-extrabold leading-[0.95] tracking-[-0.03em] text-ink text-[60px] sm:text-[92px] lg:text-[120px]">
              {t('purpose.title')}
            </h1>
          </div>
        </section>

        {/* ============ MANIFESTO (serif reading column) ============ */}
        <section className="mx-auto max-w-[920px] px-6 pt-[88px]">
          {paragraphs.map((key) => (
            <p
              key={key}
              className="mb-[22px] font-sans text-[17px] sm:text-[19px] leading-[1.75] text-ink-2 text-justify"
            >
              {t(`purpose.${key}`)}
            </p>
          ))}

          <p className="mt-11 border-t border-line-strong pt-[38px] font-serif italic font-medium text-[24px] leading-[1.4] text-ink">
            <Trans
              i18nKey="purpose.closing"
              components={{
                // brand highlight, rendered upright + yellow-04
                brand: <b className="not-italic font-normal text-yellow-04" />,
              }}
            />
          </p>
        </section>

        {/* ============ PRINCIPLES (DARE / GROW / LIFT) ============ */}
        <section className="mx-auto mt-24 max-w-[1080px] px-9 pb-28">
          <div className="mb-14 text-center">
            <h2 className="font-sans font-extrabold tracking-tight text-ink text-[28px] sm:text-[40px] leading-[1.1]">
              {t('purpose.principles_title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-7">
            {principles.map((principle, i) => (
              <PrincipleCard
                key={principle.title}
                index={i}
                title={principle.title}
                icon={principle.icon}
                description={principle.description}
              />
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PurposePage;
